<template>
    <article>
        <div class="px-4 py-5 sm:p-6">
            <h3 class="text-leonard-green">Find Your Photos</h3>
            <p>Enter your given Access Code to search for your photos.</p>
            <div class="grid grid-flow-row gap-3 mt-6">
                <label class="block">
                    <span class="text-sm">Access Code</span>
                    <input type="text"
                           id="access_code"
                           class="w-full bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey focus:outline-none focus:border-leonard-black-hover"
                           placeholder="00000000"
                           v-model="access_code"
                           v-on:keyup.enter="next"
                    >
                </label>
            </div>
            <div class="mt-4" v-if="needs_school">
                <div class="rounded-md bg-yellow-50 p-4">
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                            </svg>
                        </div>
                        <div class="ml-3">
                            <h3 class="text-sm leading-5 font-medium text-yellow-800">
                                More Information Needed
                            </h3>
                            <p class="mt-2 text-sm leading-5 text-yellow-700">
                                In order to make sure we find the correct photos, please enter the name of your school
                            </p>
                        </div>
                    </div>
                </div>
                <div class="mt-4 relative">
                    <label class="block">
                        <span class="text-sm">School Name</span>
                        <input id="school_name" class="w-full bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey-disabled focus:outline-none focus:border-leonard-black-hover" placeholder="School Name" v-model="school_name" v-on:keyup.enter="next" autocomplete="off" />
                    </label>
                    <ul id="schools" class="absolute list-group bg-leonard-white shadow">
                        <li class="p-4 list-group-item font-italic" v-if="search_results.length < 1 && !searching">
                            No results
                        </li>
                        <li class="p-4 list-group-item font-bold" v-else-if="searching">
                            <font-awesome-icon fixed-width icon="spinner" spin></font-awesome-icon>
                        </li>
                        <li class="p-4 list-group-item hover:cursor-pointer hover:bg-gray-100 hover:text-black" v-for="(item, index) in search_results" :key="index" @click.prevent="resultDetails(index)">
                            {{item.sch_name}}
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div class="bg-leonard-grey px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse sm:items-center rounded-md">
            <span class="flex w-full sm:ml-3 sm:w-auto">
                <button type="button" class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-leonard-green text-leonard-white hover:bg-leonard-green-hover hover:border-transparent focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed" @click="next" :disabled="loading" aria-label="Continue to Next Page">
                    Continue
                </button>
            </span>
            <span class="mt-3 flex w-full sm:mt-0 sm:w-auto" v-if="needs_school">
                <button type="button" class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-transparent border-leonard-green text-leonard-green hover:bg-leonard-green-hover hover:border-transparent hover:text-leonard-white focus:ring-leonard-green cursor-pointer" @click="cancel" aria-label="Cancel School Entry">
                    Reset
                </button>
            </span>
            <div class="flex mt-4 justify-center sm:justify-left sm:mt-0 sm:mr-auto" v-if="loading && !foundImages">
                <div class="flex-shrink-0">
                    <font-awesome-icon fixed-width icon="spinner" pulse></font-awesome-icon>
                </div>
                <p class="ml-3 text-leonard-disclaimer">Searching for photos...</p>
            </div>
            <div class="flex mt-4 justify-center sm:justify-left sm:mt-0 sm:mr-auto" v-if="foundImages && !alert.show">
                <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-leonard-success" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    </svg>
                </div>
                <div class="ml-2">
                    <h6 class="text-leonard-success">Success!</h6>
                    <p class="text-leonard-success">Photos Found! Please Wait...</p>
                </div>
            </div>
            <div class="flex mt-4 justify-center items-center sm:justify-left sm:mt-0 sm:mr-auto" v-if="alert.show">
                <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-leonard-error" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                    </svg>
                </div>
                <div class="ml-2">
                    <h6 class="text-leonard-error">{{alert.title}}</h6>
                    <p class="text-leonard-error">{{alert.text}}</p>
                </div>
            </div>
        </div>
    </article>
</template>

<script>
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { faSpinner } from '@fortawesome/free-solid-svg-icons';

    import Order from '../../classes/Order';
    import School from '../../classes/School';

    export default {
        props: ['pageError'],

        data() {
            return {
                School: new School(),
                Order: new Order(),
                access_code: localStorage.getItem('access_code') || '',
                search_results: [],
                alert: {},
                school_name: '',
                school_id: null,
                needs_school: false,
                loading: false,
                searching: false,
                foundImages: false,
            }
        },

        created() {
            library.add(faSpinner);
        },

        mounted() {
            const self = this;
            const inputDelegate = this.delegate('input');
            const listDelegate = this.delegate('li');

            window.events.$emit('finishedLoading');
            this.$emit('resetStudent');

            document.addEventListener('keyup', self.delay(inputDelegate(self.keyupSearch), 500));
            document.addEventListener('focusin', inputDelegate(self.showResults));
            document.addEventListener('focusout', inputDelegate(self.hideResults));
            document.addEventListener('mousedown', listDelegate(self.preventHide));

            if(this.$route.query.auto) {
                this.next()
            }
        },

        destroyed() {
            const self = this;
            const inputDelegate = this.delegate('input');
            const listDelegate = this.delegate('li');

            document.removeEventListener('keyup', inputDelegate(self.keyupSearch));
            document.removeEventListener('focusin', inputDelegate(self.showResults));
            document.removeEventListener('focusout', inputDelegate(self.hideResults));
            document.removeEventListener('mousedown', listDelegate(self.preventHide));
        },

        methods: {
            keyupSearch(el) {
                if(el.target.matches('#school_name') && this.school_name.length >= 3) {
                    this.searching = true;
                    this.search_results = [];

                    // Run the search if
                    this.searchSchools();
                }
            },

            showResults(el) {
                if(el.target.matches('#school_name')) {
                    document.querySelector('#schools').style.display = "block";
                }
            },

            hideResults(el) {
                if(el.target.matches('#school_name')) {
                    document.querySelector('#schools').style.display = "none";
                }
            },

            preventHide(el) {
                if(el.target.matches('.list-group-item')) {
                    el.preventDefault();
                }
            },

            cancel() {
                this.needs_school = false;
                this.setAlert(false);
            },

            resultDetails(index) {
                this.school_id = this.search_results[index].schnum;
                this.school_name = this.search_results[index].sch_name;
                document.querySelector('#schools').style.display = "none";
                this.$el.querySelector('div>span>button').focus();
            },

            setLoading(val) {
                this.loading = val;
            },

            setAlert(show = false, title = null, message = null) {
                this.alert = {
                    show: show,
                    title: title || 'Error',
                    text: message || 'Invalid Access Code'
                };
            },

            searchSchools() {
                let self = this;
                let data = {
                    user_id: null, // must be null to search all schools
                    search: this.school_name
                };

                this.School.searchSchools(data).then(function(response) {
                    if(response.data.length > 0) {
                        self.search_results = response.data;
                    } else {
                        self.search_results = [];
                    }

                    self.searching = false;
                })
            },

            next() {
                this.setAlert(false);
                this.setLoading(true);

                // remove hyphens before submission
                this.access_code = this.access_code.replace(/-/g,'')

                let self = this;
                let data = {
                    user_id: this.$auth.user().details.id,
                    access_code: this.access_code
                };

                if (this.needs_school) {
                    data.schnum = this.school_id;
                }

                this.Order.checkAccess(data)
                    .then(response => {
                        let data = response.data;

                        if (!response.success) {
                            self.setAlert(true, 'Error', 'An error occurred during the response. Please try again.');
                            return null;
                        }

                        if (data === null) {
                            if (response.message === 'need school') {
                                self.needs_school = true;
                            } else {
                                self.setAlert(true, 'Error', response.message);
                            }
                        } else {
                            self.foundImages = true;
                            localStorage.setItem('access_code', data.access_code);
                            localStorage.setItem('school', data.schnum);
                            localStorage.setItem('yearbook_cutoff', data.ybkcutoff);
                        }

                        return data;
                    })
                    .then(data => {
                        if (data?.access_code) {
                            self.$emit('nextStep', {
                                cutoff: data.ybkcutoff,
                                hasStudioContract: data.has_studio_access ?? false
                            });
                        }
                    })
                    .catch(error => {
                        console.error('An error occurred:', error);
                        self.setAlert(true, 'Error', 'An unexpected error occurred. Please try again.');
                    })
                    .finally(() => {
                        self.setLoading(false);
                    });
            }
        },

        watch: {
            pageError(errObj) {
                this.foundImages = false;
                this.setAlert(true, errObj.title, errObj.message);
            },
        },
    }
</script>

<style lang="scss" scoped>
    .list-group {
        position: absolute;
        top: 35px;
        left: 1.5%;
        border-radius: 0.25em;
        display:none;
        width: 97%;
        max-height: 300px;
        overflow-y: auto;
        z-index: 1;
    }
</style>
